import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useRecipe } from '../context/RecipeContext';
import landingBackground from '../images/landingbackground3.jpg';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  const handleGetStarted = () => {
    setMode('generate');
    navigate('/composer');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: { xs: '40vh', md: '50vh' },
          backgroundImage: `url(${landingBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(1px)',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 2,
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 1,
            }}
          >
            Your Privacy Matters to Us
          </Typography>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            textAlign: 'center',
            mb: 4,
            fontWeight: 'bold',
            color: 'primary.main'
          }}
        >
          How We Protect Your Information
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mb: 4,
            textAlign: 'center',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6,
            fontSize: { xs: '1rem', md: '1.1rem' }
          }}
        >
          This Privacy Policy describes how Create A Dish collects, uses, and protects your personal information
          when you use our website and services.
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Information We Collect</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ mb: 2 }}>
                We collect information you provide directly to us, such as when you create an account, use our services,
                or contact us for support. This may include:
              </Typography>
              <Typography component="div" variant="body2" sx={{ pl: 2 }}>
                • Name and email address<br />
                • Recipe preferences and search history<br />
                • Device information and usage data<br />
                • Cookies and similar tracking technologies
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">How We Use Your Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ mb: 2 }}>
                We use the information we collect to:
              </Typography>
              <Typography component="div" variant="body2" sx={{ pl: 2 }}>
                • Provide and improve our services<br />
                • Personalize your experience<br />
                • Send you updates and marketing communications (with your consent)<br />
                • Ensure security and prevent fraud<br />
                • Comply with legal obligations
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Information Sharing</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ mb: 2 }}>
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </Typography>
              <Typography component="div" variant="body2" sx={{ pl: 2 }}>
                • With your explicit consent<br />
                • To comply with legal obligations<br />
                • To protect our rights and safety<br />
                • With trusted service providers who assist our operations (under strict confidentiality agreements)
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Data Security</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access,
                alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security assessments.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Your Rights</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ mb: 2 }}>
                You have the right to:
              </Typography>
              <Typography component="div" variant="body2" sx={{ pl: 2 }}>
                • Access and update your personal information<br />
                • Request deletion of your data<br />
                • Opt out of marketing communications<br />
                • Request data portability<br />
                • Lodge a complaint with supervisory authorities
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Contact Us</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                If you have any questions about this Privacy Policy or our data practices, please contact us at{' '}
                <Typography component="span" sx={{ fontWeight: 'bold' }}>info@aisuretech.com</Typography>.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h5"
            component="h3"
            sx={{
              mb: 3,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            Ready to Create Your Perfect Dish?
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<StarIcon />}
            onClick={handleGetStarted}
            sx={{
              minWidth: 200,
              fontSize: '1.1rem',
              py: 1.5,
              px: 4,
            }}
          >
            Get Started
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            © 2025 Create A Dish. Made with ❤️ for food lovers everywhere.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;